//NPM LIBRARIES
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from "react-router-dom";
import { Col, Row } from 'reactstrap';
import { AUcallout } from '@gov.au/callout';
import { AUformGroup } from '@gov.au/form';
import queryString from 'query-string';
import moment from 'moment';
import axios from 'axios';

//REDUX ACTIONS AND SELECTORS
import {
    getFormServiceStatusSelector,
    getFormPropertiesSelector,
    getFormDataSelector
} from '../FormStore/Selectors/FormDataSelectors';
import { addPageAlert } from '../PortalStore/Actions/PageAlertActions';
import { validateHmac } from '../FormStore/Actions/FormServiceActions/GenericFormServiceActions/ValidateHmacAction';
import { additionalPaymentSubmission } from '../FormStore/Actions/FormServiceActions/RequestPayment/AdditionalPaymentSubmission';
import { getUserDetailsSelector } from '../PortalStore/Selectors/UserDetailsSelectors';
import { getOrganisationDetails, FETCH_ORGANISATIONDETAILS_SUCCESS } from '../PortalStore/Actions/OrganisationDetailsActions';

//COMPONENTS AND UTILITIES
import CASASpinner from '../components/Portal/CASASpinner';
import { SimpleItemSummary } from '../components/CASAForm/CustomComponents/SimpleItemSummary';
import Application from '../components/CASAForm/Application';
import Step from '../components/CASAForm/Step';
import Question from '../components/CASAForm/Question';
import SuccessScreen from '../pages/SuccessScreen';
import { getSinglePaymentSubmission } from '../FormStore/Actions/FormServiceActions/RequestPayment/GetSinglePaymentSubmissionAction';
import { getDashboardUrl, getSubmissionDashboardUrl } from "../components/CASAForm/Utilities";

const declarationText =
    <>
        <p>
            I hereby declare that the particulars set out in this declaration are true in every respect.
            I acknowledge that to knowingly make a false or misleading statement in this declaration is
            an offence against the Criminal Code Act 1995 (Cth).
  </p>
        <p>
            I must send the required documentation(s) and payment. The Civil Aviation (Fees) Regulations
            do not exempt me from paying the costs incurred by CASA if I withdraw my application, the permission
            I applied for is refused by CASA or your job is closed due to lack of requirements or response to CASA’s requests.
  </p>
        <p>
            I am aware that I have 30 days to pay this fee from the date this payment was requested. I am aware that this fee
            is only an estimate and that additional payment may be required.
  </p>
        <p>
            I am aware and accept the risk that information sent electronically may be intercepted and read during transmission,
            not delivered or modified.
  </p>
        <p>
            I have read <a href="https://www.casa.gov.au/node/37026">CASA’s policy on the collection and release of information</a> and
    I authorise CASA to release information contained on this form in accordance with that policy.
  </p>
    </>


const buildTableBody = (submission) => {
    const lineItems = submission.serviceRequestData.find(serviceRequestData => serviceRequestData.name == "LineItems");

  if (lineItems?.data) {
    const lineItemsDeserialized = JSON.parse(unescape(lineItems.data).replace(/\\"/g, '"'));
    
    return lineItemsDeserialized.map((item, index) => {
      return ({
        id: index,
        description: item.reason,
        feeCode: item.feeCode,
        dueDate: moment(submission.expiryDate, "YYYY-MM-DDTHH:mm:ssZ", true).format("DD MMM YYYY"), 
        fee: "$" + item.amountDue
      })
    })
  }
}

const RequestAdditionalPayment = () => {
    const dispatch = useDispatch();

    const { organisationId, submissionId } = useParams();

    const [loaderActive, setLoaderActive] = useState(true);
    const [formEnabled, setFormEnabled] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [orgLoaded, setOrgLoaded] = useState(false);

    // Redux store properties
    const B2CSettings = useSelector(state => state.B2CSettings.items);
    const submissionDetails = useSelector(state => getFormServiceStatusSelector(state.formStore, 'getAdditionalPaymentSubmissions'));
    const paymentSubmission = useSelector(state => getFormServiceStatusSelector(state.formStore, 'selectedPaymentSubmission'));

    const validateHmacResponse = useSelector(state => getFormServiceStatusSelector(state.formStore, 'validateHmac'));
    const submissionResponse = useSelector(state => getFormServiceStatusSelector(state.formStore, 'additionalPaymentSubmission'));
    const formProperties = useSelector(state => getFormPropertiesSelector(state.formStore));
    const formData = useSelector(state => getFormDataSelector(state.formStore, 'genericFormData'));
    const organisationDetails = useSelector(state => state.organisationDetails);
    const userEmail = useSelector(state => getUserDetailsSelector(state, "emailaddress1"));

    const tableHeader = [
        // eslint-disable-next-line react/display-name
        { title: "Description", key: "description", width: "50" },
        { title: "Fee code", key: "feeCode", width: "10" },
        { title: "Payment due", key: "dueDate", width: "10" },
        { title: "Fee", key: "fee", width: "10" }
    ];

    //#region UseEffect Hooks

    useEffect(() => {
        const webappurl = B2CSettings.webappurlPortalApi;
        const accessToken = B2CSettings.accesstokenPortal;

        axios.all([
            dispatch(getOrganisationDetails(webappurl, accessToken, organisationId)),

        ])
            .then(axios.spread((orgdetailsres) => {
                if (orgdetailsres.type === FETCH_ORGANISATIONDETAILS_SUCCESS
                ) {
                    setOrgLoaded(true);
                }
                else {
                    setLoaderActive(false);

                    let msg = "We're sorry. Something has gone wrong. Try again. If the error persists contact the CASA Help Desk.";
                    let details = "Details: APO.1";
                    dispatch(addPageAlert({ level: "error", title: msg, message: details }));
                }
            }))
            .catch(function (error) {
                setLoaderActive(false);

                dispatch(addPageAlert({ level: "error", title: "System error APO.1" }));
            });

    }, []);

    // Query submission details if they don't already exist
    useEffect(() => {
        if (orgLoaded) {
            const urlParameters = queryString.parse(window.location.search);

            if (urlParameters?.summaryCode === "0") {
                dispatch(validateHmac(B2CSettings.webappurlFormsApi, B2CSettings.accesstokenForms, urlParameters))
            }
            else {
                if (submissionDetails.wasSuccessful) {
                    setSelectedSubmission(submissionDetails.data.find(submission => submission.submissionId === submissionId));
                    setLoaderActive(false);
                    setFormEnabled(true);
                }
                else {
                    dispatch(getSinglePaymentSubmission(
                        B2CSettings.webappurlFormsApi,
                        B2CSettings.accesstokenForms,
                        submissionId,
                        organisationDetails.items.value[0] ? organisationDetails.items.value[0].accountid : null,
                        organisationDetails.items.value[0] ? organisationDetails.items.value[0].casa_arn : null
                    ));
                }
            }
        }
    }, [orgLoaded]);

    // Query submission details if they don't already exist
    useEffect(() => {
        if (paymentSubmission.wasSuccessful) {
            setLoaderActive(false);
            setSelectedSubmission(paymentSubmission.data);
            setFormEnabled(true);
        }
        else if (paymentSubmission.errors) {
            // TODO: This error message doesn't make sense
            dispatch(addPageAlert({
                level: "error",
                title: "Unable to complete application online",
                message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
            }));
            setLoaderActive(false);
        }
    }, [paymentSubmission]);

    // Handle Request additional payment submission
    useEffect(() => {
        if (submissionResponse.wasSuccessful && submissionResponse.data?.securePaymentToken) {
            window.location.href = B2CSettings.WestpacQuickWebURL + submissionResponse.data.securePaymentToken;
        }
        else if (submissionResponse.errors) {
            dispatch(addPageAlert({
                level: "error",
                title: "Unable to complete application online",
                message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
            }));
        }
    }, [submissionResponse]);

    // Handle result from validate HMAC
    useEffect(() => {
        if (validateHmacResponse.fetchDataSucceeded) {
            setLoaderActive(false);

            dispatch(addPageAlert({
                level: 'success',
                title: 'Payment received successfully'
            }));
        }
        else if (validateHmacResponse.fetchDataError) {
            dispatch(addPageAlert({
                level: "error",
                title: "Unable to complete application online",
                message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
            }));
            setLoaderActive(false);
        }
    }, [validateHmacResponse]);

    // Submit on button click
    useEffect(() => {
        if (formProperties.submitForm) {
            requestPaymentSubmission();
        }
    }, [formProperties.submitForm]);

    //#endregion

    const requestPaymentSubmission = async () => {
        if (formData) {
            dispatch(additionalPaymentSubmission(
                B2CSettings.webappurlFormsApi,
                B2CSettings.accesstokenForms,
                formProperties,
                formData,
                submissionId,
                declarationText)
            )
        }
    }

    if (validateHmacResponse.fetchDataSucceeded) {
        const  orgid= organisationId ? organisationDetails.items.value[0].accountid : '';
        return (
            <SuccessScreen
                orgid={orgid}>
                <div>
                    <p>We have received your payment successfully. We will contact you if we require more information on your application.</p>
                    <p>A receipt has been sent to your email address <strong>{organisationDetails.items.value[0]?.emailaddress1 ?? userEmail}</strong>.</p>
                    <p>Your application will be processed within five business days.</p>
                    <p>You can view the status of your submission anytime by viewing the <a href={getSubmissionDashboardUrl(organisationDetails.items.value[0]?.accountid)}>Submission Summary</a>  section in myCASA</p>
                </div>
            </SuccessScreen>
        );
    }

    if (loaderActive) {
        return <CASASpinner />
    }

    const getBackLinkUrl = () => {
        let backLinkUrl = '/forms' + getDashboardUrl(organisationId);
        if (submissionDetails?.data?.length > 1) {
            backLinkUrl = '/forms' + (organisationId && ('/organisation/' + organisationId)) + '/submissions/payment'
        }
        return backLinkUrl;
    }

    return (
        <>
            {submissionResponse.loading && <CASASpinner message="Submitting form" />}
            <Application
                formInformation={{
                    id: "additionalPayment",
                    name: "Payment request",
                    title: "Payment request",
                    rootFormCode: "PAY",
                    formCode: "PAY",
                    formDataType: "genericFormData",
                    version: "01",
                    organisationId,
                    organisationArn: organisationId ? organisationDetails.items.value[0].casa_arn : null
                }}
                enableSaveButton={false}
                showback={false}
                enableForm={formEnabled} >

                <Step
                    id="summary"
                    key="summary"
                    initialStep
                    isLastStep
                    showback={true}
                    backurl={getBackLinkUrl()}
                    submitText="Proceed to payment">

                    <Row noGutters>
                        <Col lg={12} md={12} className="float-none">
                            <AUcallout
                                className="full-width"
                                title=""
                                srOnlyTitle>
                                <p>If you have any questions regarding this payment item please <a href="https://www.casa.gov.au/about-us/contact-us">contact CASA</a></p>
                            </AUcallout>
                        </Col>

                        {
                            selectedSubmission &&
                            <>
                                <Col lg={12} md={12} className="float-none">
                                    <SimpleItemSummary
                                        caption="Summary"
                                        tableHeader={tableHeader}
                                        tableBody={buildTableBody(selectedSubmission)} />
                                </Col>

                                <Col lg={12} md={12} className="float-none">
                                    <AUformGroup>
                                        <h5 style={{ textAlign: "right" }}><strong>Total ${selectedSubmission.totalAmountDue}</strong></h5>
                                    </AUformGroup>
                                </Col>
                            </>
                        }
                    </Row>

                    <Question
                        id="declaration"
                        type="declaration"
                        name="declaration"
                        label="I agree and understand the above conditions."
                        value="declaration"
                        stepid="summary"
                        col={12} >
                        {declarationText}
                    </Question>

                </Step>

                <AUformGroup>
                    <p>You will be directed to Westpac for payment</p>
                </AUformGroup>

            </Application>
        </>
    );
};

RequestAdditionalPayment.propTypes = {
    dispatch: PropTypes.func,
    B2CSettings: PropTypes.object,
    envdata: PropTypes.object,
    previousPageLink: PropTypes.string,
    formProperties: PropTypes.object,
    verificationViewData: PropTypes.object,
    match: PropTypes.object
};

export default RequestAdditionalPayment;